// Web Push subscription helpers — register a Service Worker, request permission,
// subscribe via the browser PushManager using the backend's VAPID public key,
// and POST the resulting subscription to /api/push/web/subscribe.

import { api } from "@/lib/api";

const SW_PATH = "/service-worker.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function ensureSWRegistered() {
  if (!pushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  return await navigator.serviceWorker.register(SW_PATH);
}

export async function getPushStatus() {
  if (!pushSupported()) return { supported: false, subscribed: false, permission: "unsupported" };
  const reg = await ensureSWRegistered();
  if (!reg) return { supported: false, subscribed: false, permission: "unsupported" };
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    subscribed: !!sub,
    permission: Notification.permission,
  };
}

export async function enableWebPush() {
  if (!pushSupported()) {
    throw new Error("This browser does not support web push notifications.");
  }
  const reg = await ensureSWRegistered();
  if (!reg) throw new Error("Service Worker registration failed.");

  // Ask permission only if not yet granted/denied.
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  // Already subscribed? Push it to backend (idempotent) and we're done.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/web/public-key");
    if (!data?.public_key) {
      throw new Error("Server did not return a VAPID public key.");
    }
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }

  const raw = sub.toJSON();
  await api.post("/push/web/subscribe", {
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
  });
  return { subscribed: true, permission };
}

export async function disableWebPush() {
  if (!pushSupported()) return { subscribed: false };
  const reg = await ensureSWRegistered();
  if (!reg) return { subscribed: false };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { subscribed: false };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (e) { /* ignore */ }
  try {
    await api.post("/push/web/unsubscribe", { endpoint });
  } catch (e) { /* ignore — best effort */ }
  return { subscribed: false };
}

// Allow the SW to send 'navigate' messages back to the app when a notification
// is clicked but `client.navigate()` is unavailable (older browsers).
export function attachSWMessageRouter(navigate) {
  if (!pushSupported() || !navigator.serviceWorker) return () => {};
  const handler = (event) => {
    if (event?.data?.type === "navigate" && event.data.url) {
      try {
        const u = new URL(event.data.url, window.location.origin);
        if (u.origin === window.location.origin) {
          navigate(u.pathname + u.search);
        }
      } catch (e) { /* ignore */ }
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
